/**
 * El sello que Business Central deja cuando se borra un borrador que ya
 * intentó registrarse.
 *
 * No es una venta: es el rastro de un documento que se borró. Su única línea
 * lleva la descripción «Documento borrado», importe 0 y ninguna cantidad. Pero
 * **es indistinguible de una venta en todos los campos por los que el terminal
 * enruta**. Medido en el ambiente de pruebas de Ecuador el 04/09/2026, el sello
 * `PVMDSC1R-007680` contra la venta real `PVMDSC1R-007678` del mismo día y la
 * misma caja:
 *
 * | Campo | Sello | Venta |
 * |---|---|---|
 * | `Tienda` | MALL DEL SOL | MALL DEL SOL |
 * | `TPV` | PVMDSC1 | PVMDSC1 |
 * | `Turno` | 1 | 1 |
 * | `ID_Cajero` | CM | CM |
 * | `Venta_TPV` | true | true |
 * | `Cancelled` / `Anulado_TPV` | false | false |
 *
 * Por eso hace falta este módulo y no basta con mirar el importe: el sello pasa
 * el filtro de Traer Históricos igual que una venta, se trae, y **se queda con
 * los pagos huérfanos del borrador colgados** --que entran al arqueo del cierre
 * por `Pre_Assigned_No`--. Acordado en `contrato-documento-borrado-y-pagos-huerfanos`,
 * ronda 5.
 *
 * Dos reglas, y hacen falta las dos:
 *
 * 1. **La buena**: el campo `DocumentoBorradoTPV` que publica la extensión
 *    desde la 1.0.4.96. Es un booleano, se filtra y no admite interpretación.
 * 2. **La estructural**: para Honduras y El Salvador, donde el campo no llega
 *    --su entidad registrada va por otro endpoint que la extensión no puede
 *    extender--, y para cualquier ambiente que todavía no tenga la 1.0.4.96.
 */

/** El campo que publica la extensión desde la 1.0.4.96. */
export const CAMPO_SELLO = "DocumentoBorradoTPV";

/**
 * Si el documento es un sello, según el campo de Business Central.
 *
 * Se compara contra `true` a propósito: en un ambiente sin la 1.0.4.96 el campo
 * no viaja y llega `undefined`, que **no** es un sello --sólo significa que
 * nadie lo dijo--. Tratar el silencio como afirmación descartaría todas las
 * ventas del ambiente.
 */
export const esSelloPorCampo = (documento) => documento?.[CAMPO_SELLO] === true;

/**
 * Si el documento tiene forma de sello, sin preguntarle a Business Central.
 *
 * La regla es estrecha a propósito: **registrado, con líneas que suman cero, y
 * con pagos que no suman cero**. Las tres a la vez.
 *
 * - **Sólo registrado**, y esta condición no es decorativa. Una cabecera que se
 *   quedó sin sus líneas es un caso real y conocido --de ahí salió el `Id_TPV`
 *   en su día--, y queda como **borrador**: Business Central no registra un
 *   documento sin líneas, lo rechaza con «Please add at least one line item».
 *   Sin esta condición, esa cabecera rota se descartaría del listado justo
 *   cuando alguien necesita verla para arreglarla. Un sello se esconde; un
 *   documento roto se enseña.
 * - Líneas en cero *y* pagos distintos de cero, porque una venta real nunca
 *   está así: si alguien cobró, hay algo que vender. Un documento anulado en
 *   cero tampoco, porque no tiene pagos. Y el descuento del 100 % no existe en
 *   ningún país del alcance, así que no hay venta legítima con esta forma.
 *
 * No se usa la descripción «Documento borrado» de la línea: es texto libre, y
 * un criterio que compara cadenas se rompe con un cambio de idioma o de
 * mayúsculas que nadie considerará un cambio funcional.
 *
 * @param {boolean} estaRegistrado Si el documento está contabilizado en BC.
 * @param {number} importeDeLineas Suma de las líneas devueltas para el documento.
 * @param {number} importeDePagos  Suma de sus pagos.
 */
export const tieneFormaDeSello = (estaRegistrado, importeDeLineas, importeDePagos) =>
  Boolean(estaRegistrado) &&
  Math.abs(Number(importeDeLineas) || 0) < 0.01 &&
  Math.abs(Number(importeDePagos) || 0) >= 0.01;

/**
 * Quita los sellos de una lista de documentos registrados traída de BC.
 *
 * Va **antes** de pedir líneas y pagos, y esa colocación no es indiferente: los
 * pagos huérfanos anteriores a este acuerdo --30 medidos en Ecuador, 3.301,37
 * en ocho series de caja-- no traen `Documento_Definitivo`, así que
 * `descartarPagosDeOtroDocumento` los conserva por su primera rama pase lo que
 * pase. Si el sello se quita después de pedirlos, ya entraron.
 *
 * @param {Array<object>} documentos Documentos registrados tal como llegan de BC.
 * @returns {{documentos: Array<object>, sellos: Array<object>}}
 */
export const apartarSellos = (documentos) => {
  const sellos = [];
  const limpios = [];

  for (const documento of documentos ?? []) {
    if (esSelloPorCampo(documento)) sellos.push(documento);
    else limpios.push(documento);
  }

  return { documentos: limpios, sellos };
};
